import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  CreditCard,
  type LucideIcon,
  Terminal,
  Users,
} from "lucide-react";
import type * as React from "react";

import { getAdminOrgCreditBalance } from "@ando/core";
import { Button, PencilCard, cn } from "@ando/ui";

/**
 * OrgSetupChecklist — the SOTA "minimal create → configure-in-context" pairing:
 * a guided setup card on the org detail (Overview) that both shows STATUS and
 * routes to the right surface for each onboarding step. Not a nag — it doubles
 * as quick-actions once the tenant is live.
 *
 * Steps: (1) Membri — add the customer's users; (2) Accesso console — set a
 * member to ``platform-dev`` (in Admin → Utenti, a global surface role);
 * (3) Billing — default is postpaid + global markup (already valid); configure
 * prepaid/markup/credits in the Billing tab.
 */

type StepState = "done" | "todo" | "action";

function StepRow({
  icon: Icon,
  title,
  status,
  state,
  cta,
  onCta,
}: {
  icon: LucideIcon;
  title: React.ReactNode;
  status: React.ReactNode;
  state: StepState;
  cta: React.ReactNode;
  onCta: () => void;
}): React.ReactElement {
  const StateIcon = state === "done" ? CheckCircle2 : CircleDashed;
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="flex min-w-0 items-center gap-3">
        <StateIcon
          size={18}
          strokeWidth={2}
          aria-hidden
          className={cn("shrink-0", state === "done" ? "text-accent" : "text-ink-3")}
        />
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-1.5 font-medium text-[13px] text-ink-1">
            <Icon size={13} strokeWidth={1.75} aria-hidden className="text-ink-3" />
            {title}
          </span>
          <span className="truncate text-[11.5px] text-ink-3">{status}</span>
        </span>
      </span>
      <Button variant="secondary" size="sm" onClick={onCta}>
        {cta}
        <ArrowRight size={13} aria-hidden />
      </Button>
    </div>
  );
}

export function OrgSetupChecklist({
  orgId,
  memberCount,
}: {
  orgId: string;
  memberCount: number | null;
}): React.ReactElement {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const credit = useQuery({
    queryKey: ["admin", "org", "credit-balance", orgId],
    queryFn: () => getAdminOrgCreditBalance(orgId),
    staleTime: 60_000,
  });

  const goTab = (tab: "members" | "billing") =>
    void navigate({
      to: "/admin/orgs/$orgId",
      params: { orgId },
      search: { tab },
      replace: true,
    });

  const hasMembers = (memberCount ?? 0) > 0;
  const prepaid = credit.data?.is_prepaid ?? false;
  const balance = credit.data?.balance_cents ?? 0;
  const billingStatus = prepaid
    ? i18n._(msg`Prepagato · saldo ${(balance / 100).toFixed(2)} USD`)
    : i18n._(msg`Postpagato (default) · eredita il markup globale`);

  return (
    <PencilCard className="flex flex-col gap-1 p-5">
      <div className="mb-1 flex flex-col gap-0.5">
        <span className="font-medium text-[13px] text-ink-1">
          <Trans>Setup del tenant</Trans>
        </span>
        <span className="text-[11.5px] text-ink-3">
          <Trans>
            Configura e attiva questo cliente. I default sono già validi — personalizza solo ciò che
            serve.
          </Trans>
        </span>
      </div>

      <div className="flex flex-col divide-y divide-hairline">
        <StepRow
          icon={Users}
          title={<Trans>Membri</Trans>}
          state={hasMembers ? "done" : "todo"}
          status={
            hasMembers ? (
              <Trans>{memberCount ?? 0} membri nell'organizzazione</Trans>
            ) : (
              <Trans>Aggiungi gli utenti del cliente</Trans>
            )
          }
          cta={<Trans>Gestisci</Trans>}
          onCta={() => goTab("members")}
        />
        <StepRow
          icon={Terminal}
          title={<Trans>Accesso console</Trans>}
          state="action"
          status={<Trans>Imposta un membro come "Console (dev)" negli Utenti</Trans>}
          cta={<Trans>Utenti</Trans>}
          onCta={() => void navigate({ to: "/admin/users" })}
        />
        <StepRow
          icon={CreditCard}
          title={<Trans>Billing</Trans>}
          state={prepaid ? "done" : "action"}
          status={credit.isLoading ? i18n._(msg`Caricamento…`) : billingStatus}
          cta={<Trans>Configura</Trans>}
          onCta={() => goTab("billing")}
        />
      </div>
    </PencilCard>
  );
}
